import moment from "moment";
import _ from "lodash";

export type Writeable<T> = { -readonly [P in keyof T]: Writeable<T[P]> };

export type ClonedObject<ModelObject, PropContent> = {
    [K in keyof ModelObject]: ModelObject[K] extends any[]
        ? ModelObject[K][number] extends object
            ? ClonedObject<ModelObject[K][number], PropContent>[]
            : PropContent
        : ModelObject[K] extends object
        ? ClonedObject<ModelObject[K], PropContent>
        : PropContent;
};

export default class FormatUtility {
    private constructor() {}

    public static updateNestedPropertyValue = <T extends object>(
        viewModel: T,
        name: string,
        value: string | number | boolean | Date
    ): T => {
        //  console.log("viewModel", viewModel);
        // console.log("name", name);
        //  console.log("value", value);
        let castedValue = FormatUtility.castValue(value, _.get(viewModel, name));
        _.set(viewModel, name, castedValue);
        //  console.log("viewModel1", viewModel);
        return viewModel;
    };

    public static castValue = (
        value: string | number | boolean | Date,
        prop: string | number | boolean | Date
    ): string | number | boolean | Date => {
        if (!value) {
            value = "";
        }
        if (value.constructor.name !== prop.constructor.name) {
            switch (prop.constructor.name) {
                case "String":
                    value = value.toString();
                    break;
                case "Number":
                    value = parseFloat(value.toString());
                    if (isNaN(value)) {
                        value = 0;
                    }
                    break;
                case "Boolean":
                    try {
                        value = Boolean(JSON.parse(value.toString()));
                    } catch (ex) {
                        value = false;
                    }
                    break;
                case "Date":
                    value = moment(value.toString()).toDate();
                    if (isNaN(value.getTime())) {
                        value = new Date();
                    }
                    break;
            }
        }
        return value;
    };

    public static monthsDuration = (months: number | string) => {
        months = parseInt(months.toString());
        return months.toString() + " mesi";
    };

    public static percentage = (percentage: number | string) => {
        percentage = parseFloat(percentage.toString());
        return (
            percentage
                .toFixed(2)
                .replace(".", ",")
                .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " %"
        );
    };

    public static currency = (amount: number | string) => {
        amount = parseFloat(amount.toString());
        return (
            "€ " +
            amount
                .toFixed(2)
                .replace(".", ",")
                .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
        );
    };

    public static numberWithSeparators = (input: number | string, step: number | string): string => {
        if (typeof input === "number") {
            input = input.toString().replace(".", ",");
        }
        if ((input.match(/[.]/g) || []).length === 1 && (input.match(/[,]/g) || []).length === 0) {
            input = input.replace(".", ",");
        }
        let result = input
            .toString()
            .replace(/[^0-9,]/g, "")
            .replace(",", ".");
        if (result === "") {
            return "";
        }
        let decimals = 0;
        if (step) {
            let dotPos = step.toString().indexOf(".");
            if (dotPos > -1) {
                decimals = step.toString().substring(dotPos).length - 1;
                let resultDotPos = result.indexOf(".");
                if (resultDotPos > -1 && result.length > resultDotPos + decimals + 1) {
                    result = result.substring(0, resultDotPos + decimals + 1);
                }
            }
        }
        let number = parseFloat(result);
        if (isNaN(number)) {
            number = 0;
        }
        result = number
            .toFixed(decimals)
            .replace(".", ",")
            .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        return result;
    };

    public static numberFromNumberWithSeparators = (input: string): number | undefined => {
        if (input) {
            let number = parseFloat(
                input
                    .toString()
                    .replace(/[^0-9,]/g, "")
                    .replace(",", ".")
            );
            if (isNaN(number) === false) {
                return number;
            }
        }
        return undefined;
    };

    public static dateOrUndefined = (date: Date | moment.Moment | string): Date | undefined => {
        if (date) {
            let dateFormat = "YYYY-MM-DD";
            let dateFormatted = date;
            if (date instanceof Date || moment.isMoment(date)) {
                dateFormatted = moment(date).format(dateFormat);
            } else {
                if (date.indexOf("_") > -1) {
                    return undefined;
                }
                let yearLength = 0;
                switch (date.length) {
                    case 10:
                    case 19:
                        yearLength = 4;
                        break;
                    case 8:
                        yearLength = 2;
                        break;
                    default:
                        return undefined;
                    //break;
                }
                if (date.indexOf("-") === yearLength && date.lastIndexOf("-") === yearLength + 3) {
                    dateFormat = "Y".repeat(yearLength) + "-MM-DD";
                } else if (date.indexOf("/") === 2 && date.lastIndexOf("/") === 5) {
                    dateFormat = "DD/MM/" + "Y".repeat(yearLength);
                } else {
                    return undefined;
                }
                dateFormatted = date;
            }
            let result = moment(dateFormatted, dateFormat);
            if (result.isValid()) {
                return result.toDate();
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    };

    public static dateInGivenFormat = (input: Date | string, formatDest: string = "YYYY-MM-DD"): string => {
        let date = FormatUtility.dateOrUndefined(input);
        if (date != null) {
            return moment(date).format(formatDest);
        }
        return "";
    };

    public static getOmocodiaCodiceFiscale = (codiceFiscale: string, omocodia: number = 0): string => {
        const omocodiaChars = "LMNPQRSTUV";
        const chNumericiPerOmocodia = [14, 13, 12, 10, 9, 7, 6];
        if (omocodia > 0) {
            const chPos = chNumericiPerOmocodia[omocodia - 1];
            let codiceFiscaleSostitutivo = "";
            codiceFiscaleSostitutivo += codiceFiscale.substring(0, chPos);
            const numero = parseInt(codiceFiscale.substring(chPos, chPos + 1));
            if (isNaN(numero) === false) {
                codiceFiscaleSostitutivo += omocodiaChars.charAt(numero);
            } else {
                return "";
            }
            codiceFiscaleSostitutivo += codiceFiscale.substring(chPos + 1);
            codiceFiscale = codiceFiscaleSostitutivo;
            omocodia--;
        }
        return codiceFiscale;
    };

    public static getCodiceFiscale = (
        cognome: string,
        nome: string,
        dataNascita: Date,
        sesso: "M" | "F",
        luogoNascita: string
    ): string => {
        const regexConsonanti = /[^BCDFGHJKLMNPQRSTVWXYZ]/gi;
        const regexVocali = /[^AEIOU]/gi;
        const emptyChar = "X";
        const monthsChars = "ABCDEHLMPRST";
        const chDispari = {
            0: 1,
            1: 0,
            2: 5,
            3: 7,
            4: 9,
            5: 13,
            6: 15,
            7: 17,
            8: 19,
            9: 21,
            A: 1,
            B: 0,
            C: 5,
            D: 7,
            E: 9,
            F: 13,
            G: 15,
            H: 17,
            I: 19,
            J: 21,
            K: 2,
            L: 4,
            M: 18,
            N: 20,
            O: 11,
            P: 3,
            Q: 6,
            R: 8,
            S: 12,
            T: 14,
            U: 16,
            V: 10,
            W: 22,
            X: 25,
            Y: 24,
            Z: 23,
        };
        const chPari = {
            0: 0,
            1: 1,
            2: 2,
            3: 3,
            4: 4,
            5: 5,
            6: 6,
            7: 7,
            8: 8,
            9: 9,
            A: 0,
            B: 1,
            C: 2,
            D: 3,
            E: 4,
            F: 5,
            G: 6,
            H: 7,
            I: 8,
            J: 9,
            K: 10,
            L: 11,
            M: 12,
            N: 13,
            O: 14,
            P: 15,
            Q: 16,
            R: 17,
            S: 18,
            T: 19,
            U: 20,
            V: 21,
            W: 22,
            X: 23,
            Y: 24,
            Z: 25,
        };
        const chControllo = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        let partCognome = "";
        let partNome = "";
        let partAnno = "";
        let partMese = "";
        let partGiorno = "";
        let partCheckCh = "";
        let codiceFiscale = "";

        // #region parameters validation
        cognome = cognome ? cognome.toUpperCase() : "";
        nome = nome ? nome.toUpperCase() : "";
        luogoNascita = luogoNascita ? luogoNascita.toUpperCase() : "";
        // #endregion parameters validation

        // #region cognome
        partCognome = cognome.replace(regexConsonanti, "");
        partCognome += cognome.replace(regexVocali, "");
        partCognome += emptyChar.repeat(3);
        partCognome = partCognome.substr(0, 3);
        // #endregion cognome

        // #region nome
        partNome = nome.replace(regexConsonanti, "");
        // ignora la seconda consonante se il nome contiene più di 3 consonanti
        if (partNome.length > 3) {
            partNome = partNome.substring(0, 1) + partNome.substring(2, 4);
        }
        partNome += nome.replace(regexVocali, "");
        partNome += emptyChar.repeat(3);
        partNome = partNome.substr(0, 3);
        // #endregion nome

        // #region data nascita e sesso
        const data = FormatUtility.dateInGivenFormat(dataNascita, "DD/MM/YYYY");
        if (data) {
            const dd = data.substring(0, 2);
            const mm = data.substring(3, 5);
            const yy = data.substring(8, 10);

            switch (sesso) {
                case "M":
                    partGiorno = dd;
                    break;
                case "F":
                    partGiorno = (parseInt(dd) + 40).toString().padStart(2, "0");
                    break;
            }

            partMese = monthsChars.charAt(parseInt(mm) - 1);

            partAnno = yy;
        }
        // #endregion data nascita e sesso

        codiceFiscale = partCognome + partNome + partAnno + partMese + partGiorno + luogoNascita;

        if (codiceFiscale.length !== 15) {
            return "";
        }

        // #region calcola carattere di controllo
        let somma = 0;
        for (let i = 0; i < 15; i++) {
            const char = codiceFiscale.charAt(i);
            if (i % 2) {
                somma += chPari[char];
            } else {
                somma += chDispari[char];
            }
        }
        somma %= 26;
        partCheckCh = chControllo.charAt(somma);
        // #endregion calcola carattere di controllo

        codiceFiscale += partCheckCh;

        if (codiceFiscale.length !== 16) {
            return "";
        }

        return codiceFiscale;
    };

    //#region find object fields
    public static getNestedFieldsValues = <T extends object>(obj: T, fieldName: string, result: any[]) => {
        if (fieldName in obj) {
            result.push(obj[fieldName]);
        }
        Object.keys(obj).forEach((key: string) => {
            if (typeof obj[key] === "object") {
                FormatUtility.getNestedFieldsValues(obj[key], fieldName, result);
            }
        });
    };
    //#endregion find object fields

    // #region clone object functions
    public static cloneObject = <T, O = {}>(object: T, replaceValuesFunction?: (val: T) => O) => {
        return FormatUtility.deepCloneElement<T, O>(object, replaceValuesFunction);
    };

    private static deepCloneElement = <T, O = {}>(
        value: T,
        replaceValuesFunction?: (val: T) => O
    ): ClonedObject<T, O> | unknown => {
        if (typeof value !== "object" || value === null) {
            if (replaceValuesFunction !== undefined) {
                return replaceValuesFunction(value);
            } else {
                return value;
            }
        } else if (Array.isArray(value)) {
            return FormatUtility.deepCloneArray(value, replaceValuesFunction);
        } else {
            return FormatUtility.deepCloneObject(value, replaceValuesFunction);
        }
    };

    private static deepCloneObject = <T, O>(source: T, replaceValuesFunction?: (val: T) => O): ClonedObject<T, O> => {
        const excludedKeys = ["IS_BASE_MODEL", "sjsId", "sjsOptions"];
        const result = {};
        Object.keys(source)
            .filter((key) => excludedKeys.includes(key) === false)
            .forEach((key) => {
                const value = source[key];
                result[key] = FormatUtility.deepCloneElement<T, O>(value, replaceValuesFunction);
            }, {});
        return result as ClonedObject<T, O>;
    };

    private static deepCloneArray = <T extends any[], O>(
        collection: T,
        replaceValuesFunction?: (val: T) => O
    ): ClonedObject<T, O> => {
        return collection.map((value) => {
            return FormatUtility.deepCloneElement<T, O>(value, replaceValuesFunction);
        }) as ClonedObject<T, O>;
    };
    // #endregion clone object functions
}
